/**
 * WelcomeModal Component
 *
 * Shown once to first-time users after sign-in (Google/Kakao).
 * Guides them to either upload a photo or start chatting.
 *
 * @author Heseda AI Team
 * @version 1.1
 */

import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const SKINCARE_INTERESTS = [
  { id: 'kbeauty', emoji: '🇰🇷', labelKey: 'welcome.interests.kbeauty' },
  { id: 'acne', emoji: '💧', labelKey: 'welcome.interests.acne' },
  { id: 'antiAging', emoji: '✨', labelKey: 'welcome.interests.antiAging' },
  { id: 'sensitive', emoji: '🧴', labelKey: 'welcome.interests.sensitive' },
  { id: 'brightening', emoji: '☀️', labelKey: 'welcome.interests.brightening' },
  { id: 'hydration', emoji: '💦', labelKey: 'welcome.interests.hydration' },
];

interface WelcomeModalProps {
  onStartChat: (interests?: string) => void;
  onUploadPhoto: (interests?: string) => void;
}

function WelcomeModal({ onStartChat, onUploadPhoto }: WelcomeModalProps) {
  const { t } = useTranslation(['modals']);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);

  // Move focus into the dialog on mount for accessibility
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const toggleInterest = (id: string) => {
    setSelectedInterests((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const buildInterestsMessage = (): string | undefined => {
    if (selectedInterests.length === 0) return undefined;
    const labels = selectedInterests.map((id) => {
      const item = SKINCARE_INTERESTS.find((i) => i.id === id);
      return item ? t(item.labelKey) : id;
    });
    return t('welcome.interestsMessage', { interests: labels.join(', ') });
  };

  return (
    <div className="modal-overlay" data-testid="welcome-modal">
      <div
        className="modal-content welcome-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-title"
        aria-describedby="welcome-subtitle"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="welcome-modal__header">
          <span className="welcome-modal__wave" aria-hidden="true">👋</span>
          <h3 id="welcome-title" ref={headingRef} tabIndex={-1}>{t('welcome.title')}</h3>
          <p id="welcome-subtitle" className="welcome-modal__subtitle">{t('welcome.subtitle')}</p>
        </div>

        <div className="welcome-modal__steps">
          <div className="welcome-modal__step">
            <span className="welcome-modal__step-icon" aria-hidden="true">📸</span>
            <div>
              <strong>{t('welcome.photoTitle')}</strong>
              <p>{t('welcome.photoDescription')}</p>
            </div>
          </div>
          <div className="welcome-modal__divider">{t('welcome.or')}</div>
          <div className="welcome-modal__step">
            <span className="welcome-modal__step-icon" aria-hidden="true">💬</span>
            <div>
              <strong>{t('welcome.chatTitle')}</strong>
              <p>{t('welcome.chatDescription')}</p>
            </div>
          </div>
        </div>

        <div className="welcome-modal__interests">
          <p className="welcome-modal__interests-label">{t('welcome.interestsLabel')}</p>
          <div className="welcome-modal__interests-grid">
            {SKINCARE_INTERESTS.map((interest) => (
              <button
                key={interest.id}
                type="button"
                className={`welcome-modal__interest-chip${selectedInterests.includes(interest.id) ? ' welcome-modal__interest-chip--selected' : ''}`}
                onClick={() => toggleInterest(interest.id)}
                aria-pressed={selectedInterests.includes(interest.id) ? 'true' : 'false'}
              >
                <span aria-hidden="true">{interest.emoji}</span> {t(interest.labelKey)}
              </button>
            ))}
          </div>
        </div>

        <div className="welcome-modal__actions">
          <button
            className="modal-btn modal-btn--primary"
            onClick={() => onUploadPhoto(buildInterestsMessage())}
            data-testid="welcome-upload-photo"
          >
            <span aria-hidden="true">📸</span> {t('welcome.uploadPhoto')}
          </button>
          <button
            className="modal-btn modal-btn--secondary"
            onClick={() => onStartChat(buildInterestsMessage())}
            data-testid="welcome-start-chat"
          >
            <span aria-hidden="true">💬</span> {t('welcome.startChatting')}
          </button>
        </div>

        <p className="welcome-modal__footer">{t('welcome.footer')}</p>
      </div>
    </div>
  );
}

export default WelcomeModal;
